/**
 * Convert conflict value from backend format (0/1) to display format (No/Yes)
 * @param {number|string|boolean} value - Conflict value from backend
 * @returns {string} "Yes" or "No"
 */
export function formatConflictDisplay(value) {
  return hasConflict(value) ? 'Yes' : 'No';
}

const isDeletedFlag = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (value === 1 || value === '1' || value === true) return true;
  const strValue = String(value).toLowerCase();
  return strValue === 'true' || strValue === 'yes' || strValue === 'y';
};

const isActiveStatus = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (value === 1 || value === '1' || value === true) return true;
  if (value === 0 || value === '0' || value === false) return false;
  const strValue = String(value).toLowerCase();
  if (strValue === 'active' || strValue === 'yes' || strValue === 'y' || strValue === 'true') return true;
  if (strValue === 'inactive' || strValue === 'no' || strValue === 'n' || strValue === 'false') return false;
  return false;
};

const hasConflictCount = (value) => {
  const numValue = Number(value);
  return Number.isFinite(numValue) && numValue > 0;
};

const hasConflictFromObject = (value) => {
  const nestedConflicts =
    value.conflicts ??
    value.songConflicts ??
    value.song_conflicts ??
    value.conflictRecords ??
    value.conflict_records;

  if (nestedConflicts !== undefined) {
    return hasConflict(nestedConflicts);
  }

  if (
    hasConflictCount(value.activeConflictCount) ||
    hasConflictCount(value.conflictCount) ||
    hasConflictCount(value.conflictsCount)
  ) {
    return true;
  }

  if (value.conflict !== undefined) {
    return hasConflict(value.conflict);
  }

  // Treat object as a single conflict record when it carries lifecycle flags.
  if (
    Object.prototype.hasOwnProperty.call(value, 'status') ||
    Object.prototype.hasOwnProperty.call(value, 'is_delete') ||
    Object.prototype.hasOwnProperty.call(value, 'is_deleted')
  ) {
    const deleted = isDeletedFlag(value.is_delete ?? value.is_deleted ?? value.isDelete ?? value.isDeleted);
    const active = isActiveStatus(value.status ?? value.conflictStatus ?? value.conflict_status);
    return active && !deleted;
  }

  return false;
};

/**
 * Check if conflict badge should show error style
 * @param {number|string|boolean} value - Conflict value from backend
 * @returns {boolean} true if there's a conflict
 */
export function hasConflict(value) {
  if (value === undefined || value === null || value === '') return false;

  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return value.some((item) => hasConflict(item));
  }

  if (typeof value === 'object') {
    return hasConflictFromObject(value);
  }

  if (value === 1 || value === '1' || value === true) return true;
  if (value === 0 || value === '0' || value === false) return false;
  
  const strValue = String(value).toLowerCase();
  return strValue === 'yes' || strValue === 'y' || strValue === 'active' || strValue === 'true';
}

/**
 * Convert ownership filter value to display percentage
 * If the value is "High" or "Low", calculate representative percentage
 * If already a number/percentage, return as-is
 * @param {string|number} value - Ownership value
 * @returns {string} Ownership percentage
 */
export function formatOwnershipDisplay(value) {
  if (value === undefined || value === null) return '0%';
  
  // If it's already a number or percentage string, return it
  if (typeof value === 'number') return `${value}%`;
  
  const strValue = String(value).toLowerCase();
  if (strValue === 'high') return '≥50%';
  if (strValue === 'low') return '<50%';
  
  // If it already contains %, return as-is
  if (String(value).includes('%')) return String(value);
  
  // Try to parse as number
  const numValue = Number(value);
  if (!isNaN(numValue)) return `${numValue}%`;
  
  return String(value);
}
